import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Events } from "ionic-angular";
import Diagnosis, {
  DiagnosisStatus,
  PALLIATIVE_ENCOUNTER_DIAGNOSIS_CODE,
} from "models/patient-chart/diagnosis";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { DiagnosisService } from "providers/patient-charts/diagnosis-service";
import { take } from "rxjs/operators";
import IcdCode from "../../../../models/codes/IcdCode";
import ContentWrapper from "../../../../models/content-wrapper/content-wrapper";
import {
  AssessmentInfo,
  AssessmentStatuses,
} from "../../../../models/patient-chart/assessmentInfo";
import { AssessmentDataService } from "../../../../providers/asmt-data-service/asmt-data-service";
import { ReferralsServiceProvider } from "../../../../providers/patient-charts/referrals-service";

@Component({
  selector: "diagnosis-card",
  templateUrl: "diagnoses-card.html",
})
export class DiagnosesCard implements OnInit {
  @Input() config;

  dynamicFormData: any = {};

  visitDiagnosisForm: FormGroup;
  label: string;
  icdSearchResults: any[][] = [];
  hideIcdCodeDropdownMenu: any = [];
  displayAddButton: boolean = false;
  diagnosisData: any;
  isLoading: boolean = true;
  patientChartId: number = null;
  inProgressDiagnoses: Diagnosis[] = [];
  inProgressDiagnosesOnLoad: Diagnosis[] = [];
  previousActiveDiagnoses: Diagnosis[] = [];
  solvedInProgressDiagnoses: Diagnosis[] = [];
  assessmentDiagnoses: Diagnosis[] = [];
  initialized: boolean = false;
  primaryDiagnosis: boolean = false;
  values: object = {};
  dynamicFormValues: object = {};
  hasDuplicateDiagnosisLocal: boolean = false;
  warningMessage: string;
  unifiedDiagnosisPlan: boolean = false;

  @Input() patChartId;
  @Input() assessmentId;
  @Input() disabled: boolean;
  @Input() enabledAfterCompletion: boolean;
  @Output() hasDuplicateDiagnosis: EventEmitter<boolean> = new EventEmitter();
  @Output() onDiagnosesAdd: EventEmitter<any> = new EventEmitter();
  @Output() onDiagnosesRemove: EventEmitter<any> = new EventEmitter();
  @Output() onDiagnosesResolve: EventEmitter<any> = new EventEmitter();
  @Output() onDataUpdate: EventEmitter<any> = new EventEmitter();
  @Output() onCardInitialized: EventEmitter<any> = new EventEmitter();
  @Output() validityEvent = new EventEmitter<boolean>();

  @ViewChild("yourDiv") yourDiv: ElementRef;

  constructor(
    public _fb: FormBuilder,
    public referralService: ReferralsServiceProvider,
    private navBarService: NavBarServiceProvider,
    public diagnosisService: DiagnosisService,
    public events: Events,
    public dataService: AssessmentDataService,
    private renderer: Renderer2
  ) {}

  private sortData(data) {
    data.sort(function (a, b) {
      return a.id - b.id;
    });
  }
  private createDiagnosisFormGroup(): FormGroup {
    return new FormGroup({
      diagnosis: new FormControl(""),
      comment: new FormControl(""),
    });
  }
  private createDiagnosisOnSelectIcdCode(icdCode: IcdCode) {
    const assessmentInfo = new AssessmentInfo();
    assessmentInfo.assessmentId = this.assessmentId;
    assessmentInfo.status = AssessmentStatuses.InProgress;
    const diagnosis = new Diagnosis();
    diagnosis.id = 0;
    diagnosis.diagnosisType = "VISIT";
    diagnosis.icdCode = icdCode;
    diagnosis.assessmentInfo = assessmentInfo;
    diagnosis.patientChartId = this.patientChartId;
    // TODO: Figure out the rest of these properties..
    return diagnosis;
  }
  private handleDiagnoses = (diagnoses: Diagnosis[]) => {
    let visitDiagnoses: Diagnosis[] = diagnoses.filter(
      (x) => x.diagnosisType.toLowerCase() == "visit"
    );
    visitDiagnoses = visitDiagnoses.filter(
      (d: Diagnosis) => d.icdCode.code != PALLIATIVE_ENCOUNTER_DIAGNOSIS_CODE
    );
    this.sortData(visitDiagnoses);
    // Diagnoses that will be displayed in textbox
    this.inProgressDiagnoses = visitDiagnoses.filter(this.isInProgress);

    if (this.disabled || this.enabledAfterCompletion) {
      this.inProgressDiagnoses = visitDiagnoses.filter(this.wasInProgress);
    }

    if (this.inProgressDiagnoses.length > 0) {
      for (let i = 0; i < this.inProgressDiagnoses.length; i++) {
        if (i > 0) {
          this.addDiagnosisInput();
        }
        this.visitDiagnosisForm
          .get("diagnoses")
          ["controls"][i].controls.diagnosis.setValue(
            `${this.inProgressDiagnoses[i].icdCode.description} (${this.inProgressDiagnoses[i].icdCode.code})`
          );
        this.visitDiagnosisForm
          .get("diagnoses")
          ["controls"][i].controls.comment.setValue(
            this.inProgressDiagnoses[i].comment
          );
        this.displayAddButton = true;
      }
    }
    // Diagnoses that will be displayed in grid with checkmark
    this.solvedInProgressDiagnoses = visitDiagnoses.filter(
      this.isResolvedInProgress
    );
    if (this.disabled || this.enabledAfterCompletion) {
      this.solvedInProgressDiagnoses = visitDiagnoses.filter(
        this.wasResolvedInProgress
      );
    }
    this.assessmentDiagnoses = visitDiagnoses.filter(
      (d: Diagnosis) => this.isActive(d) || this.isResolvedInProgress(d)
    );
    if (this.disabled || this.enabledAfterCompletion) {
      this.assessmentDiagnoses = visitDiagnoses.filter(
        (d: Diagnosis) => this.wasActive(d) || this.wasResolvedInProgress(d)
      );
    }
    this.assessmentDiagnoses = this.assessmentDiagnoses.filter(
      (d: Diagnosis) => d.icdCode.code != PALLIATIVE_ENCOUNTER_DIAGNOSIS_CODE
    );
    this.onDiagnosesAdd.emit({
      cardId: this.config.id,
      value: this.inProgressDiagnoses,
      isLoading: this.isLoading,
    });
    this.onDataUpdate.emit(this.getCardValues());
    this.solvedInProgressDiagnoses.forEach((d) => {
      this.handleResolve({ checked: true }, d);
    });
  };

  private isInProgress = (diagnosis: Diagnosis): boolean => {
    return (
      diagnosis.assessmentInfo != null &&
      diagnosis.assessmentInfo.assessmentId <= parseInt(this.assessmentId) &&
      diagnosis.assessmentInfo.status == DiagnosisStatus.IN_PROGRESS
    );
  };
  private wasInProgress = (diagnosis: Diagnosis): boolean => {
    return (
      diagnosis.assessmentInfo != null &&
      diagnosis.assessmentInfo.assessmentId == parseInt(this.assessmentId)
    );
  };

  private isActive = (diagnosis: Diagnosis): boolean => {
    return (
      diagnosis.assessmentInfo != null &&
      diagnosis.assessmentInfo.assessmentId <= parseInt(this.assessmentId) &&
      diagnosis.assessmentInfo.status == DiagnosisStatus.ACTIVE
    );
  };
  private wasActive = (diagnosis: Diagnosis): boolean => {
    return (
      diagnosis.assessmentInfo != null &&
      diagnosis.assessmentInfo.assessmentId < parseInt(this.assessmentId) &&
      (diagnosis.assessmentInfo.solvedAssessmentId == null ||
        parseInt(this.assessmentId) <
          diagnosis.assessmentInfo.solvedAssessmentId)
    );
  };

  private isResolvedInProgress = (diagnosis: Diagnosis): boolean => {
    return (
      diagnosis.assessmentInfo != null &&
      diagnosis.assessmentInfo.assessmentId <= parseInt(this.assessmentId) &&
      (diagnosis.assessmentInfo.status ==
        DiagnosisStatus.RESOLVED_IN_PROGRESS ||
        (diagnosis.assessmentInfo.status == DiagnosisStatus.RESOLVED &&
          diagnosis.assessmentInfo.solvedAssessmentId == null))
    );
  };
  private wasResolvedInProgress = (diagnosis: Diagnosis): boolean => {
    return (
      diagnosis.assessmentInfo != null &&
      diagnosis.assessmentInfo.solvedAssessmentId == parseInt(this.assessmentId)
    );
  };

  private findDiagnosesDataForVisit(patientChartId) {
    this.diagnosisService.getDiagnosisDataForVisit(patientChartId).subscribe(
      (data) => {
        if (data) {
          data = data.filter((d: Diagnosis) => d != null);
        } else {
          data = [];
        }
        this.handleDiagnoses(data);
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        console.log(error.message);
      }
    );
  }

  public ngOnInit(): void {
    this.unifiedDiagnosisPlan =
      this.config.inputs && this.config.inputs.plan !== "undefined";
    this.events.subscribe("setPatientChartId", (patientChartId) => {
      if (this.patientChartId === null) {
        this.findDiagnosesDataForVisit(patientChartId);
        this.patientChartId = patientChartId;
      }
    });
    const patientChartId = this.navBarService.getPatientChartId();
    if (patientChartId) {
      this.patientChartId = patientChartId;
      this.patChartId = patientChartId;
      this.getPatientChartID().then((result) => {
        this.findDiagnosesDataForVisit(patientChartId);
      });
    }
    this.label = this.config.cardName;
    this.visitDiagnosisForm = this._fb.group({
      diagnoses: this._fb.array([this.createDiagnosisFormGroup()]),
      primaryDiagnosis: new FormControl(""),
    });
    this.checkDuplicateDiagnosis(true);
    this.renderer.listen("document", "click", (result) => {
      if (!this.yourDiv.nativeElement.contains(result.target)) {
        this.hideIcdCodeDropdownMenu = this.hideIcdCodeDropdownMenu.map(
          (x) => true
        );
      }
    });
  }

  public initializeDiagnoses(): void {
    this.findDiagnosesDataForVisit(this.patientChartId);
  }

  public addDiagnosisInput() {
    const diagnoses = this.visitDiagnosisForm.get("diagnoses") as FormArray;
    const lastSelectedDiagnosisIndex = diagnoses.length - 1;
    if (this.inProgressDiagnoses[lastSelectedDiagnosisIndex]) {
      diagnoses.push(this.createDiagnosisFormGroup());
    }
  }

  public removeDiagnosis(i: number, diagnosis: any) {
    const diagnoses = this.visitDiagnosisForm.get("diagnoses") as FormArray;
    if (diagnoses.length > 1) {
      diagnoses.removeAt(i);
    } else {
      diagnoses.reset();
    }

    let elementToDelete = this.inProgressDiagnoses.filter(
      (d: Diagnosis) =>
        this.getDiagnosisDisplayName(d).toString() == diagnosis.value.diagnosis
    );
    let deleted = this.inProgressDiagnoses.splice(
      this.inProgressDiagnoses.indexOf(elementToDelete[0]),
      1
    );

    if (deleted.length) {
      this.onDiagnosesRemove.emit({ cardId: this.config.id, value: deleted });
    }
    this.checkDuplicateDiagnosis(false);
  }

  public removeDiagnosisInEdit(i: number) {
    const deleted = this.inProgressDiagnoses.splice(i, 1);
    this.onDiagnosesRemove.emit({ cardId: this.config.id, value: deleted });
    this.checkDuplicateDiagnosis(false);
  }

  public getDiagnosisDisplayName(d: Diagnosis) {
    let stringRes =
      d.icdCode.description.toString() + " (" + d.icdCode.code.toString() + ")";
    return stringRes;
  }

  public isDiagnosisDuplicate(): boolean {
    return false;
  }

  public searchDiagnosis(index: number) {
    this.hideIcdCodeDropdownMenu[index] = false;
    const diagnosisInputs = this.visitDiagnosisForm.get(
      "diagnoses"
    ) as FormArray;
    const searchTerm = diagnosisInputs.at(index).value.diagnosis;
    if (searchTerm && searchTerm !== "") {
      if (searchTerm.length >= 3) {
        this.getIcdCodes(searchTerm, index);
      }
    }

    if (!searchTerm) {
      this.setNullIcdCode(index);
    }
  }

  // user should NOT be able to select Z515
  public getIcdCodes(searchTerm: string, index: number) {
    this.referralService
      .getIcdCodeSearch(searchTerm)
      .pipe(take(1))
      .subscribe((response: ContentWrapper<IcdCode>) => {
        this.icdSearchResults[index] = response.content.filter(
          (e) => e.code != PALLIATIVE_ENCOUNTER_DIAGNOSIS_CODE
        );
      });
  }

  public selectDiagnosis(icdCode: IcdCode, index: number) {
    const diagnoses = this.visitDiagnosisForm.get("diagnoses") as FormArray;
    if (
      diagnoses["controls"].length > this.inProgressDiagnoses.length &&
      index == diagnoses["controls"].length - 1
    ) {
      this.setDiagnosis(icdCode, index);
    } else {
      this.removeDiagnosisInEdit(index);
      this.setDiagnosisInEdit(icdCode, index);
    }
  }

  public setNullIcdCode(index: number) {
    this.inProgressDiagnoses[index] = {
      ...this.createDiagnosisOnSelectIcdCode(null),
      id: this.inProgressDiagnoses[index].id,
    };
    this.onDiagnosesAdd.emit({
      cardId: this.config.id,
      value: this.inProgressDiagnoses,
    });
    this.onDataUpdate.emit(this.getCardValues());
  }

  public setDiagnosisComment(comment: any, index: number) {
    this.inProgressDiagnoses[index].comment = comment.value;
    this.onDiagnosesAdd.emit({
      cardId: this.config.id,
      value: this.inProgressDiagnoses,
    });
    this.onDataUpdate.emit(this.getCardValues());
  }

  public setDiagnosis(icdCode: IcdCode, index: number) {
    this.visitDiagnosisForm
      .get("diagnoses")
      ["controls"][index].controls.diagnosis.setValue(
        `${icdCode.description} (${icdCode.code})`
      );
    this.inProgressDiagnoses[index] =
      this.createDiagnosisOnSelectIcdCode(icdCode);

    this.onDiagnosesAdd.emit({
      cardId: this.config.id,
      value: this.inProgressDiagnoses,
    });
    this.onDataUpdate.emit(this.getCardValues());
    this.hideIcdCodeDropdownMenu[index] = true;
    this.displayAddButton = true;
    if (index > 1) {
      this.visitDiagnosisForm
        .get("diagnoses")
        ["controls"][index].controls.diagnosis.setValidators([
          Validators.required,
        ]);
    }
    this.checkDuplicateDiagnosis(false);
  }

  public setDiagnosisInEdit(icdCode: IcdCode, index: number) {
    this.visitDiagnosisForm
      .get("diagnoses")
      ["controls"][index].controls.diagnosis.setValue(
        `${icdCode.description} (${icdCode.code})`
      );

    this.inProgressDiagnoses.splice(
      index,
      0,
      this.createDiagnosisOnSelectIcdCode(icdCode)
    );
    this.onDiagnosesAdd.emit({
      cardId: this.config.id,
      value: this.inProgressDiagnoses,
    });
    this.onDataUpdate.emit(this.getCardValues());
    this.visitDiagnosisForm
      .get("diagnoses")
      ["controls"][index].controls.diagnosis.setValidators([
        Validators.required,
      ]);
    this.visitDiagnosisForm
      .get("diagnoses")
      ["controls"][index].controls.diagnosis.updateValueAndValidity();
    this.hideIcdCodeDropdownMenu[index] = true;
    this.displayAddButton = true;
    if (index > 1) {
      this.visitDiagnosisForm
        .get("diagnoses")
        ["controls"][index].controls.diagnosis.setValidators([
          Validators.required,
        ]);
    }
    this.checkDuplicateDiagnosis(false);
  }

  public checkDuplicateDiagnosis(checkDB: boolean) {
    let duplicateIcdCodeCheckList: IcdCode[] = [];
    if (!checkDB) {
      duplicateIcdCodeCheckList = this.inProgressDiagnoses
        .filter((d) => d.assessmentInfo.status != "ACTIVE")
        .map((d) => d.icdCode)
        .filter((i) => i.code !== PALLIATIVE_ENCOUNTER_DIAGNOSIS_CODE);
    }
    this.diagnosisService
      .postCheckDuplicateDiagnoses(
        this.patientChartId,
        duplicateIcdCodeCheckList,
        checkDB
      )
      .take(1)
      .subscribe((exists) => {
        if (exists) {
          this.hasDuplicateDiagnosisLocal = true;
          this.hasDuplicateDiagnosis.emit(true);
          this.dataService.customErrorToast(
            "Diagnosis you selected already exist, please select a different diagnosis"
          );
        } else {
          this.hasDuplicateDiagnosisLocal = false;
          this.hasDuplicateDiagnosis.emit(false);
        }
      });
  }

  public handleResolve(event, diagnosis: Diagnosis) {
    const assessmentInfo = diagnosis.assessmentInfo;
    const status = event.checked ? "RESOLVED" : "ACTIVE";
    diagnosis.assessmentInfo = { ...assessmentInfo, status };
    if (status == "ACTIVE") {
      diagnosis.assessmentInfo.solvedAssessmentId = null;
    }
    this.onDiagnosesResolve.emit({ cardId: this.config.id, value: diagnosis });
  }

  public async getPatientChartID() {
    let result: any = await this.navBarService
      .getNewPatientChartData(this.patChartId ? this.patChartId : "", true)
      .toPromise();
    return result.id;
  }

  @Input() set data(card: any) {
    if (card) {
      if (card.data) {
        this.values = card.data[0];
        this.primaryDiagnosis = this.values["diagnosis"];

        this.values["diagnosis"] = this.primaryDiagnosis;
        this.onDataUpdate.emit({ cardId: this.config.id, value: this.values });
        this.dynamicFormData = card.data[0];
      }
    }

    if (card !== undefined && !this.initialized) {
      this.initialized = true;
      this.onCardInitialized.emit(this.config.id);
    }
  }

  onPrimaryDiagnosisChange(data) {
    this.values["diagnosis"] = data;
    this.onDataUpdate.emit(this.getCardValues());
  }
  dataChange(event) {
    this.dynamicFormValues = event["value"];
    this.onDataUpdate.emit(this.getCardValues());
  }
  validityChange(event) {
    this.validityEvent.emit(event);
  }

  diagnosisHaveComment() {
    return (
      this.inProgressDiagnoses.filter((d) => d.comment && d.comment != "")
        .length > 0
    );
  }

  getCardValues() {
    return {
      cardId: this.config.id,
      value: {
        ...this.values,
        ...this.dynamicFormValues,
        hasDiagnosisComment: this.diagnosisHaveComment(),
      },
    };
  }

  getWarningMessage() {
    if (this.unifiedDiagnosisPlan) {
      if (this.dynamicFormValues["plan"]) {
        return "";
      } else {
        let hasDiagnoses =
          this.inProgressDiagnoses && this.inProgressDiagnoses.length > 0;
        if (!hasDiagnoses) {
          return "Please add at least one diagnosis or plan";
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/diagnoses-card/diagnoses-card.ts